import React, { JSX } from 'react';
import { BiHelpCircle } from 'react-icons/bi';
import styled from 'styled-components';
import { Tooltip } from '@mui/material';
import { ChartAndMetricsWrapper, ChartWrapper } from './common';

const TOOLTIP_DELAY = 300;

export const MetricsWrapper = ({
  id,
  children,
  className,
}: {
  id?: string;
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      id={id}
      className={`pl-2 pt-3 flex lg:inline flex-row w-full sm:w-auto md:justify-end sm:flex-col sm:pt-0 sm:pl-1 sm:pb-8 ${
        className ? className : ''
      } md:mt-auto`}
    >
      {children}
    </div>
  );
};

export const IconWrapper = ({ id, children }: { id: string; children: React.ReactNode }) => {
  return (
    <div id={id} className='flex items-center pl-1 grow fill-current text-gray-600'>
      {children}
    </div>
  );
};

export const MetricTitleWrapper = ({ id, children }: { id: string; children: React.ReactNode }) => {
  return (
    <div id={id} className='flex text-base text-gray-400 h-7 justify-start'>
      {children}
    </div>
  );
};

export const ValueWrapper = ({
  color,
  children,
}: {
  color: string | undefined;
  children: React.ReactNode;
}) => {
  return (
    <div className='w-auto grow-0 text-xl sm:text-3xl md:text-5xl' style={{ color: color }}>
      {children}
    </div>
  );
};

export const MetricWrapper = ({ id, children }: { id: string; children: React.ReactNode }) => {
  return (
    <div id={id} className='flex flex-col justify-end pr-1 mb-2 overflow-hidden h-full w-auto'>
      {children}
    </div>
  );
};

const PercentWrapper = styled.div`
  font-size: 1rem;
  width: auto;
  flex-grow: 1;
  line-height: 1;
  color: #95a5a6;
  margin-left: 0.4rem;
`;

export type MetricProps = {
  value: number | string;
  title: string;
  color?: string;
  helpText: string;
  percent?: string | number | boolean;
  fontSize?: 'normal' | 'small';
  showPercent?: number | string | undefined;
};

interface ChartAndMetricsProps {
  children: React.ReactNode;
  metrics: MetricProps[];
  title: string;
  metricsTitle?: string | undefined;
  buttons?: React.ReactNode;
}

export const ChartAndMetrics = ({
  children,
  metrics,
  title,
  metricsTitle,
  buttons,
}: ChartAndMetricsProps) => {
  return (
    <div id={`chart-and-metrics-${title}`} className='flex flex-col h-full w-full'>
      <h3 id={`chart-title-${title}`} className='my-0 pb-4 pr-10 pt-0 text-gray-500'>
        {title}
        <div>{buttons}</div>
      </h3>
      <ChartAndMetricsWrapper>
        <ChartWrapper>{children}</ChartWrapper>
        <MetricsWrapper>
          {metricsTitle && <h3>{metricsTitle}</h3>}
          {metrics.map((mProps, index) => (
            <Metric key={index} {...mProps} />
          ))}
        </MetricsWrapper>
      </ChartAndMetricsWrapper>
    </div>
  );
};

const Metric = ({
  percent = false,
  value,
  title,
  color,
  helpText,
  showPercent,
}: MetricProps): JSX.Element => {
  const displayValue = value.toString() + (percent ? '%' : '');

  return (
    <MetricWrapper id='metric-with-tooltip'>
      <div className='flex w-full'>
        <ValueWrapper color={color}>{displayValue}</ValueWrapper>
        <PercentWrapper className='self-end'>{showPercent && '' + showPercent + '%'}</PercentWrapper>
      </div>
      <MetricTitleWrapper id='metric-title'>
        <p className='w-auto text-clip'>{title + ' '}</p>
        <IconWrapper id={'info-wrapper'}>
          <Tooltip title={helpText} enterDelay={TOOLTIP_DELAY} arrow>
            <div>
              <BiHelpCircle />
            </div>
          </Tooltip>
        </IconWrapper>
      </MetricTitleWrapper>
    </MetricWrapper>
  );
};

export default Metric;
